import {ProcessRunMode} from './process-run-mode.model';
import {ProcessState} from './process-state.model';
import {DaysOfWeekLong, DaysOfWeekShort} from '@app/core/utils/other-utils';

export class ProcessConfig {
    constructor(
        public incremental: boolean = false,
        public processId: number | string = null,
        public cronExpression: string = '',
        public runMode: ProcessRunMode = ProcessRunMode.STOPPED,
        public state: ProcessState = null,
        public cronConfig: CronConfig = null
    ) {

    }

    static isHoursCtrlMultiple(processConfig: ProcessConfig): boolean {
        return processConfig.incremental && processConfig.cronConfig && processConfig.cronConfig.cronOption === CronOptionType.DAILY;
    }
}

export class CronTab {
    static isEmptyValue(value: string) {
        return ((value === '?') || (value === '*'));
    }

    constructor(
        public seconds: number[],
        public minutes: number[],
        public hours: number[],
        public daysOfMonth: number[],
        public months: number[],
        public daysOfWeek: number[]
    ) {

    }

    getSecondsStr() {
        return this.getStrValue(this.seconds);
    }

    getMinutesStr() {
        return this.getStrValue(this.minutes);
    }

    getHoursStr() {
        return this.getStrValue(this.hours);
    }

    getDaysOfMonthStr() {
        // const defaultValue = (this.daysOfWeek && (this.daysOfWeek.length > 0)) ? '?' : '*';
        return this.getStrValue(this.daysOfMonth, '*');
    }

    getMonthsStr() {
        return this.getStrValue(this.months);
    }

    getDaysOfWeekStr() {
        // const defaultValue = (this.daysOfMonth && (this.daysOfMonth.length > 0)) ? '?' : '*';
        return this.getStrValue(this.daysOfWeek, '?');
    }

    getStrValue(list: number[], defaultValue: string = '*') {
        return (list && (list.length > 0)) ? list.join() : defaultValue;
    }

    public toString() {
        return [
            this.getSecondsStr(),
            this.getMinutesStr(),
            this.getHoursStr(),
            this.getDaysOfMonthStr(),
            this.getMonthsStr(),
            this.getDaysOfWeekStr()
        ].join(' ');
    }
}

export class CronConfig {
    static getCronTab(cronConfig: CronConfig): CronTab {
        const daysOfMonth = (cronConfig.cronOption >= CronOptionType.MONTHLY) ? cronConfig.daysOfMonth : null;
        const daysOfWeek = (cronConfig.cronOption >= CronOptionType.WEEKLY) ? cronConfig.daysOfWeek : null;
        return new CronTab([0], [cronConfig.minute], cronConfig.hours, daysOfMonth, null, daysOfWeek);
    }

    static getCronString(cronConfig: CronConfig): string {
        return CronConfig.getCronTab(cronConfig).toString();
    }

    static getCronConfigTimezone(cronConfig: CronConfig, timezoneOffset: number): CronConfig {
        let dayOffset = 0;
        const newHours = (cronConfig.hours || []).map((hour: number) => {
            let totalMinutesUTC = (hour * 60 + cronConfig.minute) + timezoneOffset;

            if (totalMinutesUTC < 0) {
                // prev day
                totalMinutesUTC += 24 * 60;
                dayOffset = -1;
            }

            if (totalMinutesUTC >= 24 * 60) {
                // next day
                totalMinutesUTC -= 24 * 60;
                dayOffset = 1;
            }

            return Math.floor( totalMinutesUTC / 60);
        });

        const newDaysOfMonth: number[] = (cronConfig.daysOfMonth || [])
            .map((day: number) => {
                let newDay = day + dayOffset;
                if (newDay < 1) {
                    newDay = 31;
                } else if (newDay > 31) {
                    newDay = 1;
                }
                return newDay;
            })
            .sort((a: number, b: number) => a - b);

        const newDaysOfWeek: number[] = (cronConfig.daysOfWeek || [])
            .map((day: number) => {
                let newDay = day + dayOffset;
                if (newDay < 0) {
                    newDay = 6;
                } else if (newDay > 6) {
                    newDay = 0;
                }
                return newDay;
            })
            .sort((a: number, b: number) => a - b);

        return new CronConfig(cronConfig.minute,  newHours, newDaysOfMonth, newDaysOfWeek, cronConfig.cronOption);
    }

    static getCronStringForLocalTimezone(cronConfig: CronConfig): string {
        const newCronConfig = CronConfig.getCronConfigTimezone(cronConfig, (new Date).getTimezoneOffset());
        return CronConfig.getCronTab(newCronConfig).toString();
    }

    constructor(
        public minute: number = 0,
        public hours: number[] = [0],
        public daysOfMonth: number[] = [],
        public daysOfWeek: number[] = [],
        public cronOption: CronOptionType = CronOptionType.DAILY
    ) {

    }
}

export enum CronOptionType {
    HOURLY,
    DAILY,
    WEEKLY,
    MONTHLY
}

export class CronConfigFactory {
    static createFromCronExpression(cronExpression: string, convertFromUTC = true): CronConfig {
        const cronValuesList: string[] = cronExpression.split(' ');
        if (cronValuesList.length < 6) {
            return new CronConfig();
        }

        const minute: number = (CronTab.isEmptyValue(cronValuesList[1])) ? 0 : Number(cronValuesList[1]);
        const hours: number[] = (CronTab.isEmptyValue(cronValuesList[2])) ? [] : cronValuesList[2].split(',').map(Number);
        const daysOfMonth: number[] = (CronTab.isEmptyValue(cronValuesList[3])) ? [] : cronValuesList[3].split(',').map(Number);
        const daysOfWeek = CronConfigFactory.getNumberDaysOfWeek(cronValuesList[5]);
        const cronOption: CronOptionType = (CronTab.isEmptyValue(cronValuesList[5]))
            ? ((CronTab.isEmptyValue(cronValuesList[3])) ? CronOptionType.DAILY : CronOptionType.MONTHLY)
            : CronOptionType.WEEKLY;

        let cronConfig = new CronConfig(minute, hours, daysOfMonth, daysOfWeek, cronOption);
        if (convertFromUTC) {
            cronConfig = CronConfig.getCronConfigTimezone(cronConfig, -(new Date).getTimezoneOffset());
        }
        return cronConfig;
    }

    static getNumberDaysOfWeek(daysOfWeekCronValue: string): number[] {

        let daysOfWeek: number[] = [];
        const daysOfWeekLong = DaysOfWeekLong;
        const daysOfWeekShort = DaysOfWeekShort;
        if (daysOfWeekCronValue.includes('-')) {
            const startAndFinishString: string[] = daysOfWeekCronValue.split('-');
            let startAndFinishNumbers: number[];

            if (daysOfWeekLong.includes(startAndFinishString[0].toLowerCase())) {
                startAndFinishNumbers = startAndFinishString.map(value => daysOfWeekLong.indexOf(value.toLowerCase()));
            } else {
                if (daysOfWeekShort.includes(startAndFinishString[0].toLowerCase())) {
                    startAndFinishNumbers = startAndFinishString.map(value => daysOfWeekShort.indexOf(value.toLowerCase()));
                } else {
                    startAndFinishNumbers = startAndFinishString.map(Number);
                }
            }

            for ( let i = startAndFinishNumbers[0]; i < (startAndFinishNumbers[1] + 1); i++ ) {
                daysOfWeek.push(i);
            }
        } else {

            const daysOfWeekList = (CronTab.isEmptyValue(daysOfWeekCronValue)) ? [] : daysOfWeekCronValue.split(',');

            if (daysOfWeekList?.length > 0) {
                if (daysOfWeekLong.includes(daysOfWeekList[0].toLowerCase())) {
                    daysOfWeek = daysOfWeekList.map(value => daysOfWeekLong.indexOf(value.toLowerCase()));
                } else {
                    if (daysOfWeekShort.includes(daysOfWeekList[0].toLowerCase())) {
                        daysOfWeek = daysOfWeekList.map(value => daysOfWeekShort.indexOf(value.toLowerCase()));
                    } else {
                        daysOfWeek = daysOfWeekList.map(Number);
                    }
                }
            }
        }

        return daysOfWeek;
    }
}
